/**
 * 📌 Script: DonationPickupSignatureService.js
 * 📂 Caminho: src/services/DonationPickupSignatureService.js
 * 📝 Descrição: Contrato frontend para assinatura digital de coleta.
 *
 * 🔄 Relação com o Projeto:
 *  - Será consumido pelo card de coleta de Abholspenden.
 *  - Centralizará criação de assinatura e upload privado em etapa futura.
 *
 * 📥 Entradas (Parâmetros):
 *  - Canvas, protocolo, dados de sessão interna e arquivo de assinatura.
 *
 * 📤 Saídas:
 *  - Promises futuras com path privado da assinatura.
 *
 * 🧩 Exports:
 *  - `createDonationPickupSignaturePad()` - Contrato de inicialização.
 *  - `isDonationPickupSignatureEmpty()` - Contrato de validação.
 *  - `uploadDonationPickupSignature()` - Contrato de upload.
 *  - `uploadDonationPickupSignatureDataUrl()` - Upload a partir de Base64.
 *
 * 🔗 Dependências:
 *  - **Utilitários internos:** nenhum nesta etapa.
 *  - **Componentes Vue:** futuro CardAbholspendenAbholen.vue.
 *  - **Backend (Cloud Functions/API):** spendenKunden.
 *  - **Bibliotecas externas (NPM):** signature_pad em etapa futura.
 */

import SignaturePad from "signature_pad";
import { getStorage, ref, uploadBytes } from "firebase/storage";
import app from "@/firebaseConfig";
import { cor, corRgba } from "@/services/cores";

/**
 * Inicializa a instancia do SignaturePad no canvas.
 * @param {HTMLCanvasElement} canvasElement
 * @return {SignaturePad}
 */
export function createDonationPickupSignaturePad(canvasElement) {
  if (!canvasElement) return null;

  // Padrao visual e comportamento
  return new SignaturePad(canvasElement, {
    penColor: cor("azul-escuro"), // tinta escura da marca
    backgroundColor: corRgba("branco", 0), // transparente
    minWidth: 1.5,
    maxWidth: 3,
  });
}

/**
 * Valida se a assinatura esta vazia.
 * @param {SignaturePad} pad
 * @return {boolean}
 */
export function isDonationPickupSignatureEmpty(pad) {
  if (!pad) return true;
  return pad.isEmpty();
}

/**
 * Exporta a assinatura, converte para blob e faz upload para Storage privado.
 * @param {SignaturePad} pad Instancia do pad.
 * @param {string} monthKey Chave do mes (ex: 2026-05).
 * @param {string} protocol Numero do protocolo.
 * @return {Promise<{path:string,dataUrl:string}>} Path privado e imagem.
 */
export async function uploadDonationPickupSignature(pad, monthKey, protocol) {
  if (isDonationPickupSignatureEmpty(pad)) {
    throw new Error("Assinatura vazia");
  }

  // Tenta exportar como WebP, fallback para PNG
  let dataUrl;
  let ext = "webp";
  let contentType = "image/webp";

  try {
    dataUrl = pad.toDataURL("image/webp");
  } catch (e) {
    // Se navegador nao suportar toDataURL image/webp, usa png
    dataUrl = pad.toDataURL("image/png");
    ext = "png";
    contentType = "image/png";
  }

  // Converter dataUrl para Blob
  const base64Part = dataUrl.split(",")[1];
  const binaryStr = atob(base64Part);
  const len = binaryStr.length;
  const uint8Array = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    uint8Array[i] = binaryStr.charCodeAt(i);
  }
  const blob = new Blob([uint8Array], { type: contentType });

  // Upload
  const storage = getStorage(app);
  const fileName = `signature.${ext}`;
  const storagePath = `donation-pickups/${protocol}/signature/${fileName}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, blob, {
    contentType,
    customMetadata: {
      protocol,
      monthKey,
    },
  });

  return {
    path: storagePath,
    dataUrl,
  };
}

/**
 * Faz upload da assinatura a partir de um Data URL (Base64) ja gerado.
 * Usado pela fila offline, que nao tem acesso ao objeto SignaturePad.
 * @param {string} dataUrl Assinatura em Base64 (ex: data:image/webp;base64,...).
 * @param {string} monthKey Chave do mes (ex: 2026-05).
 * @param {string} protocol Numero do protocolo.
 * @return {Promise<{path:string,dataUrl:string}>} Path privado e imagem.
 */
export async function uploadDonationPickupSignatureDataUrl(
  dataUrl,
  monthKey,
  protocol
) {
  if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:image/")) {
    throw new Error("Data URL de assinatura invalido");
  }

  // Detecta contentType e extensao a partir do proprio Data URL.
  const match = /^data:(image\/[a-zA-Z0-9+.-]+)[;,]/.exec(dataUrl);
  const contentType = match ? match[1] : "image/webp";
  const ext = contentType.split("/")[1] || "webp";

  // Converte o Data URL em Blob reutilizando o fetch nativo.
  const blob = await (await fetch(dataUrl)).blob();

  const storage = getStorage(app);
  const fileName = `signature.${ext}`;
  const storagePath = `donation-pickups/${protocol}/signature/${fileName}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, blob, {
    contentType,
    customMetadata: {
      protocol,
      monthKey,
    },
  });

  return {
    path: storagePath,
    dataUrl,
  };
}
